// Step 3 — Files. List + Add/Edit/Remove with SweetAlert2 modals.
// Document status badges (§5 Step 3) gate which rows are editable:
//   Approved / Pending / New  → editable + removable
//   Denied / Archived          → read-only

import { useMemo, useState } from "react";
import Swal from "sweetalert2";

import { useFormStore } from "../../store";
import type { DocumentRow, DocumentStatus } from "../../types";

import { openDocumentModal } from "./modals/documentModal";

const BADGE_MARKUP: Record<DocumentStatus, { cls: string; icon: string; label: string }> = {
  approved: { cls: "bg-success", icon: "fa-circle-check", label: "Approved" },
  pending: { cls: "bg-warning text-dark", icon: "fa-clock", label: "Under Review" },
  denied: { cls: "bg-danger", icon: "fa-circle-xmark", label: "Denied" },
  archived: { cls: "bg-secondary", icon: "fa-box-archive", label: "Archived" },
  new: { cls: "bg-primary", icon: "fa-plus", label: "New" },
};

type SortField = "name" | "expires" | "status";
type SortDir = "asc" | "desc";

function isReadOnly(doc: DocumentRow): boolean {
  return doc.status === "denied" || doc.status === "archived";
}

function compare(a: DocumentRow, b: DocumentRow, field: SortField): number {
  switch (field) {
    case "name":
      return (a.new_name ?? "").localeCompare(b.new_name ?? "", undefined, { sensitivity: "base" });
    case "expires":
      // Empty dates sort last regardless of direction (caller handles dir).
      if (!a.new_expiration_date && !b.new_expiration_date) return 0;
      if (!a.new_expiration_date) return 1;
      if (!b.new_expiration_date) return -1;
      return a.new_expiration_date.localeCompare(b.new_expiration_date);
    case "status":
      return (a.status ?? "").localeCompare(b.status ?? "");
  }
}

interface SortableHeaderProps {
  field: SortField;
  label: string;
  sort: { field: SortField; dir: SortDir };
  onChange: (next: { field: SortField; dir: SortDir }) => void;
}

function SortableHeader({ field, label, sort, onChange }: SortableHeaderProps) {
  const active = sort.field === field;
  const dir = active ? sort.dir : null;
  return (
    <th scope="col">
      <button
        type="button"
        className="btn btn-link p-0 text-decoration-none text-reset fw-bold"
        onClick={() =>
          onChange({
            field,
            dir: active && sort.dir === "asc" ? "desc" : "asc",
          })
        }
        aria-label={`Sort by ${label}`}
      >
        {label}
        <i
          className={`ms-1 fa-solid ${
            dir === "asc" ? "fa-sort-up" : dir === "desc" ? "fa-sort-down" : "fa-sort text-muted"
          }`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

export default function Step3Files() {
  const documents = useFormStore((s) => s.documents);
  const setDocuments = useFormStore((s) => s.setDocuments);

  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: "name",
    dir: "asc",
  });
  const [filterText, setFilterText] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  // documents.filter().map() preserving original indices for handler dispatch
  const enriched = useMemo(
    () =>
      documents
        .map((doc, index) => ({ doc, index }))
        .filter(({ doc }) => doc.action !== "delete"),
    [documents],
  );

  const totalActive = enriched.filter(({ doc }) => doc.status !== "archived").length;
  const totalArchived = enriched.filter(({ doc }) => doc.status === "archived").length;

  const visible = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    let rows = enriched;
    if (!showArchived) {
      rows = rows.filter(({ doc }) => doc.status !== "archived");
    }
    if (needle) {
      rows = rows.filter(({ doc }) => {
        const name = (doc.new_name ?? "").toLowerCase();
        const desc = (doc.new_description ?? "").toLowerCase();
        return name.includes(needle) || desc.includes(needle);
      });
    }
    rows = [...rows].sort(({ doc: a }, { doc: b }) => {
      const cmp = compare(a, b, sort.field);
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [enriched, filterText, showArchived, sort]);

  const handleAdd = async () => {
    const result = await openDocumentModal({ mode: "create" });
    if (!result || !result.new_document_url) return;
    setDocuments([
      ...documents,
      {
        id: null,
        action: "create",
        status: "new",
        new_name: result.new_name,
        new_description: result.new_description,
        new_expiration_date: result.new_expiration_date ?? undefined,
        new_document_url: result.new_document_url,
      },
    ]);
  };

  const handleEdit = async (index: number) => {
    const doc = documents[index];
    const result = await openDocumentModal({
      mode: "edit",
      initial: {
        new_name: doc.new_name,
        new_description: doc.new_description,
        new_expiration_date: doc.new_expiration_date,
      },
    });
    if (!result) return;

    const next = [...documents];
    next[index] = {
      ...doc,
      new_name: result.new_name,
      new_description: result.new_description,
      new_expiration_date: result.new_expiration_date ?? undefined,
      new_document_url: result.new_document_url ?? doc.new_document_url,
      action: doc.action === "create" ? "create" : "update",
    };
    setDocuments(next);
  };

  const handleRemove = async (index: number) => {
    const doc = documents[index];
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Remove this document?",
      text: doc.new_name ?? "",
      showCancelButton: true,
      confirmButtonText: "Remove",
      customClass: { confirmButton: "swal-btn-danger" },
    });
    if (!confirm.isConfirmed) return;

    const next = [...documents];
    if (doc.id == null) {
      next.splice(index, 1);
    } else {
      next[index] = { ...doc, action: "delete" };
    }
    setDocuments(next);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="brand-section-heading mb-0 flex-grow-1">
          <i className="fa-solid fa-file-lines" aria-hidden="true" />
          <span>
            Files
            <span className="badge bg-secondary ms-2 align-middle" aria-label={`${totalActive} active files`}>
              {totalActive}
            </span>
            {totalArchived > 0 && (
              <small className="text-muted ms-2 fw-normal">
                + {totalArchived} archived
              </small>
            )}
          </span>
        </h2>
        <button type="button" className="btn btn-primary" onClick={handleAdd}>
          <i className="fa-solid fa-plus me-1" aria-hidden="true" /> Add File
        </button>
      </div>

      {/* Toolbar: filter + show-archived toggle */}
      <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
        <div className="flex-grow-1" style={{ maxWidth: 360 }}>
          <div className="input-group input-group-sm">
            <span className="input-group-text" aria-hidden="true">
              <i className="fa-solid fa-magnifying-glass" />
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Filter by name or description…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              aria-label="Filter files"
            />
          </div>
        </div>
        {totalArchived > 0 && (
          <div className="form-check form-switch mb-0">
            <input
              type="checkbox"
              className="form-check-input"
              id="show-archived-toggle"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="show-archived-toggle">
              Show archived
            </label>
          </div>
        )}
      </div>

      <table className="table reflow-table align-middle">
        <thead>
          <tr>
            <SortableHeader field="name" label="Name" sort={sort} onChange={setSort} />
            <th scope="col">Description</th>
            <SortableHeader field="expires" label="Expires" sort={sort} onChange={setSort} />
            <SortableHeader field="status" label="Status" sort={sort} onChange={setSort} />
            <th scope="col" className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && (
            <tr>
              <td colSpan={5} className="text-muted text-center py-3">
                {filterText
                  ? "No files match your filter."
                  : enriched.length === 0
                    ? <>No files yet. Click <strong>Add File</strong> above to attach one.</>
                    : "No active files. Toggle “Show archived” to view archived items."}
              </td>
            </tr>
          )}
          {visible.map(({ doc, index }) => {
            const status = doc.status ?? "new";
            const b = BADGE_MARKUP[status];
            const readOnly = isReadOnly(doc);
            return (
              <tr key={doc.id ?? `new-${index}`}>
                <td data-label="Name">
                  {doc.new_document_url ? (
                    <a href={doc.new_document_url} target="_blank" rel="noopener noreferrer">
                      {doc.new_name}
                    </a>
                  ) : (
                    doc.new_name
                  )}
                </td>
                <td data-label="Description">{doc.new_description}</td>
                <td data-label="Expires">{doc.new_expiration_date || "—"}</td>
                <td data-label="Status">
                  <span className={`badge ${b.cls}`}>
                    <i className={`fa-solid ${b.icon} me-1`} aria-hidden="true" />
                    {b.label}
                  </span>
                </td>
                <td data-label="Actions" className="text-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary me-1"
                    disabled={readOnly}
                    title={readOnly ? "Denied/archived documents are read-only" : "Edit"}
                    onClick={() => handleEdit(index)}
                  >
                    <i className="fa-solid fa-pen" aria-hidden="true" />
                    <span className="visually-hidden">Edit</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={readOnly}
                    title={readOnly ? "Denied/archived documents are read-only" : "Remove"}
                    onClick={() => handleRemove(index)}
                  >
                    <i className="fa-solid fa-trash-can" aria-hidden="true" />
                    <span className="visually-hidden">Remove</span>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
